import React, {
  forwardRef,
  useImperativeHandle,
  useState,
  useRef,
  useMemo,
  useCallback,
  useEffect,
} from 'react';
import clsx from 'clsx';
import Input from '@/ui/Input';
import LoadingDots from '@/ui/LoadingDots';
import { ArrowRight } from 'lucide-react';
import editIcon from '@/assets/img/edit_ico.svg';
import type { EquifaxAddress } from '@/types/postcode/EquifaxAddress';
import type { AddressSuggession } from '@/types/postcode/AddressSuggession';
import useAddressLookup from '@/services/postocde/useAddressLookup';
import usePostcodeSuggestions from '@/services/postocde/usePostcodeSuggestions';
import { getCookie, getUserAnswers } from '@/utils/functions';
import { loadFromStorage, updateBasicInfo } from '@/utils/client-storage/localStorage';
import { generateSession } from '@/services/useGenerateSession';
import { getUtmTagsFromUrl } from '@/utils/utm';

export type PostcodeElementRef = {
  getData: () => {
    postcode: string;
    selectedPostcode: string;
    addresses: AddressSuggession[];
    selectedAddress?: EquifaxAddress;
  };
  getErrors: () => { error: string | null };
  validate: () => boolean;
  isValid: () => boolean;
  isLoading: () => boolean;
};

export type PostcodeElementProps = {
  onValidationChange?: (isValid: boolean) => void;
  homepageMode?: boolean;
  noPulsating?: boolean;
  title?: string | null;
  triggerQueryParams?: boolean;
  hideSelectedCard?: boolean;
  onSubmit?: () => void;
  onLoadingChange?: (loading: boolean) => void;
  onAddressesChange?: (addresses: AddressSuggession[]) => void;
  allowLocalStorageFallback?: boolean;
};

const PostcodeElement = forwardRef<PostcodeElementRef, PostcodeElementProps>(
  (
    {
      onValidationChange,
      homepageMode = false,
      noPulsating = false,
      title = null,
      triggerQueryParams = false,
      hideSelectedCard = false,
      onSubmit,
      onLoadingChange,
      onAddressesChange,
      allowLocalStorageFallback = false,
    },
    ref
  ) => {
    const [showAddressDropdown, setShowAddressDropdown] = useState(false);
    const [selectedAddress, setSelectedAddress] = useState<EquifaxAddress | undefined>(
      getUserAnswers()?.basicInfo?.selectedAddressObject
    );
    const [postcode, setPostcode] = useState(getUserAnswers()?.basicInfo?.postcode || '');
    const [selectedPostcode, setSelectedPostcode] = useState(
      getUserAnswers()?.basicInfo?.postcode || ''
    );
    const [suggestions, setSuggestions] = useState<string[]>([]);
    const [addresses, setAddresses] = useState<any[]>(
      getUserAnswers()?.basicInfo?.prefetchedAddresses || []
    );
    const [addressSearch, setAddressSearch] = useState('');
    const [isEditingAddress, setIsEditingAddress] = useState(false);
    const [error, setError] = useState('');
    const [isAddressesLoading, setIsAddressesLoading] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const addressInputRef = useRef<HTMLInputElement>(null);

    const { mutateAsync: fetchSuggestions, isPending: isSuggestionsPending } =
      usePostcodeSuggestions();

    const isLoading = isAddressesLoading || isSuggestionsPending || isSubmitting;

    useEffect(() => {
      onLoadingChange?.(isLoading);
    }, [isLoading, onLoadingChange]);
    const { mutateAsync: fetchSuggestionsD8 } = useAddressLookup();

    const shouldShowSearchButton = useMemo(() => {
      if (
        (postcode?.length > 0 && suggestions.length > 0) ||
        (postcode?.length > 0 && selectedPostcode?.length > 0)
      )
        return false;
      return true;
    }, [postcode?.length, suggestions.length, selectedPostcode?.length]);

    const fetchAddressesByPostcode = useCallback(
      async (pc: string) => {
        if (!pc) return;

        setIsAddressesLoading(true);
        try {
          const result = await fetchSuggestionsD8(
            { postal_code: pc },
            {
              onSuccess: ({ suggestions }) => {
                setIsAddressesLoading(false);
                if (!suggestions || suggestions.length === 0) {
                  setError('Please enter a valid postcode');
                } else {
                  setError('');
                  setAddresses(suggestions);
                  updateBasicInfo({ prefetchedAddresses: suggestions });
                  onAddressesChange?.(suggestions);
                  setIsEditingAddress(true);
                }
              },
              onError: (error: any) => {
                setIsAddressesLoading(false);
                const message = error?.response?.data?.message;
                if (message) {
                  setError(message);
                }
              },
            }
          );
          return result?.suggestions || [];
        } catch (err) {
          setIsAddressesLoading(false);
          console.error('Failed to fetch addresses:', err);
        }
      },
      [fetchSuggestionsD8, onAddressesChange]
    );

    const handleSuggestionSelect = useCallback(
      async (val: string) => {
        setPostcode(val);
        setSelectedPostcode(val);
        setError('');

        if (homepageMode) {
          setIsSubmitting(true);
          const fetchedAddresses = await fetchAddressesByPostcode(val);
          if (onSubmit) {
            setTimeout(() => onSubmit(), 10);
          }
          return fetchedAddresses;
        }

        setSuggestions([]);
        return await fetchAddressesByPostcode(val);
      },
      [fetchAddressesByPostcode, homepageMode, onSubmit]
    );

    const handleAddressSelect = useCallback(
      (address: EquifaxAddress) => {
        setSelectedAddress(address);
        setAddressSearch('');
        setShowAddressDropdown(false);
        setIsEditingAddress(false);
        updateBasicInfo({
          address: formatEfxAddress(address).value,
          selectedAddressObject: address,
        });

        if (error) {
          setError('');
        }
      },
      [error]
    );

    useEffect(() => {
      if (!triggerQueryParams) return;

      const initFromParams = async () => {
        const params = new URLSearchParams(window.location.search);
        const pcFromURL = params.get('postcode');
        const addr = params.get('address');

        const pc =
          pcFromURL || (allowLocalStorageFallback ? getUserAnswers()?.basicInfo?.postcode : null);

        if (pc) {
          const result: any = await handleSuggestionSelect(pc);
          if (addr && result?.length > 0) {
            const normalizedAddr = addr.toLowerCase();
            const match = result.find((s: any) => s.label?.toLowerCase().includes(normalizedAddr));

            if (match) {
              handleAddressSelect(match.value);
            } else {
              setAddressSearch(addr);
              setIsEditingAddress(true);
              setShowAddressDropdown(true);
            }
          }
        }
      };

      initFromParams();
    }, [triggerQueryParams, handleAddressSelect, handleSuggestionSelect]);

    const handlePostcodeSearchWithValue = useCallback(
      async (searchValue: string) => {
        const clean = searchValue.trim().toUpperCase();
        if (!clean) {
          setError('Please enter a postcode');
          return;
        }

        setError('');
        setSuggestions([]);

        const claimNumber = loadFromStorage('currentId') || getCookie('currentId');
        if (!claimNumber) {
          try {
            await generateSession({ utmTags: JSON.stringify(getUtmTagsFromUrl()) });
          } catch (error) {
            console.error('Failed to generate session:', error);
          }
        }

        try {
          await fetchSuggestions(clean, {
            onSuccess: (suggessions) => {
              if (!suggessions.length) {
                setError('Please enter a valid postcode');
                return;
              }

              if (suggessions.length === 1) {
                handleSuggestionSelect(suggessions[0]);
              } else {
                setSuggestions(suggessions);
              }
            },
            onError: (error) => {
              setError('Please enter a valid postcode');
              console.error('Postcode search error:', error);
            },
          });
        } catch (error) {
          setError('Please enter a valid postcode');
          console.error('Postcode search error:', error);
        }
      },
      [fetchSuggestions, handleSuggestionSelect]
    );

    const handlePostcodeSearch = useCallback(async () => {
      await handlePostcodeSearchWithValue(postcode);
    }, [postcode, handlePostcodeSearchWithValue]);

    const handleAddressInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      setAddressSearch(value);
      setIsEditingAddress(true);

      if (error) {
        setError('');
      }

      if (addresses.length > 0) {
        setShowAddressDropdown(true);
      }
    };

    const handleAddressInputClick = () => {
      if (addresses.length > 0) {
        setShowAddressDropdown(true);
      }
    };

    const handleArrowClick = (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (selectedAddress && !isEditingAddress) {
        setIsEditingAddress(true);
        setAddressSearch('');
        setShowAddressDropdown(true);
      } else {
        if (showAddressDropdown && selectedAddress) {
          setIsEditingAddress(false);
          setShowAddressDropdown(false);
        } else {
          setShowAddressDropdown(!showAddressDropdown);
        }
      }
    };

    const isValid = useMemo(() => Boolean(selectedAddress && !error), [selectedAddress, error]);

    const filteredAddresses = useMemo(() => {
      if (!addressSearch.trim()) return addresses;
      return addresses.filter((addr) =>
        addr.label?.toLowerCase().includes(addressSearch.toLowerCase())
      );
    }, [addresses, addressSearch]);

    const validate = useCallback(() => {
      const postcodeRegex = /^[A-Z]{1,2}[0-9R][0-9A-Z]? [0-9][ABD-HJLNP-UW-Z]{2}$/i;
      const cleanPostcode = postcode.trim();

      if (!cleanPostcode) {
        setError('Please enter a postcode');
        return false;
      }

      const isValidFormat = postcodeRegex.test(cleanPostcode);
      if (!isValidFormat) {
        setError('Please enter a valid UK postcode (e.g. W10 4AA)');
        return false;
      }

      if (homepageMode) {
        setError('');
        return true;
      }

      if (!selectedAddress) {
        setError('Please select an address');
        return false;
      }

      setError('');

      updateBasicInfo({
        postcode: cleanPostcode,
      });

      return true;
    }, [postcode, homepageMode, selectedAddress, addresses]);

    useImperativeHandle(
      ref,
      () => ({
        getData: () => ({
          postcode,
          selectedPostcode,
          addresses,
          selectedAddress,
        }),
        getErrors: () => ({ error: error || null }),
        validate: () => validate(),
        isValid: () => isValid,
        isLoading: () => isAddressesLoading || isSuggestionsPending || isSubmitting,
      }),
      [
        postcode,
        selectedPostcode,
        addresses,
        selectedAddress,
        error,
        validate,
        isValid,
        isAddressesLoading,
        isSuggestionsPending,
        isSubmitting,
      ]
    );

    useEffect(() => {
      onValidationChange?.(isValid);
    }, [isValid, onValidationChange]);

    return (
      <>
        {!homepageMode && (
          <label htmlFor="postcode" className="block text-subtitle font-medium text-gray-900 mb-3">
            {title || 'Current Postcode'}
          </label>
        )}

        <div
          className={clsx(
            'w-full flex items-center justify-between border rounded-lg md:rounded-2xl p-1.5 md:p-3 transition-all bg-white min-h-[64px] md:min-h-[82px]',
            error ? 'border-red-500' : 'border-primary'
          )}
        >
          <div className="flex-1 pl-3 pr-2 py-1">
            <label className="input-eyebrow-label mb-1 md:mb-1.5">ENTER YOUR POSTCODE</label>
            <input
              value={postcode}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                let value = e.target.value.toUpperCase().replace(/[^A-Z0-9 ]/g, '');
                value = value.replace(/\s+/g, '');
                if (value.length > 8) value = value.slice(0, 8);
                if (value.length > 3) {
                  value = value.slice(0, value.length - 3) + ' ' + value.slice(value.length - 3);
                }
                setPostcode(value);
                updateBasicInfo({ postcode: value });
                setSelectedPostcode('');
                if (error) setError('');
                if (value !== selectedPostcode) {
                  setSuggestions([]);
                  setAddresses([]);
                  setSelectedAddress(undefined);
                  setIsEditingAddress(false);
                  setShowAddressDropdown(false);
                }
              }}
              placeholder="e.g. W10 4AA"
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handlePostcodeSearch();
                }
              }}
              className="w-full text-title font-bold text-[#111827] placeholder-gray-500 placeholder:text-subtitle placeholder:font-medium focus:outline-none bg-transparent h-8 md:h-10"
            />
          </div>

          {(homepageMode || (!selectedAddress && shouldShowSearchButton)) && (
            <button
              aria-label="Search Postcode"
              onClick={() => {
                handlePostcodeSearch();
              }}
              disabled={isLoading}
              className={clsx(
                'w-[48px] h-[48px] md:w-[58px] md:h-[58px] rounded-lg md:rounded-xl flex items-center justify-center transition-all bg-primary hover:bg-[#1e8549]',
                postcode.trim() && !isLoading && !noPulsating && 'pulsating-btn'
              )}
            >
              <ArrowRight className="text-white w-7 h-7 md:w-9 md:h-9" strokeWidth={3} />
            </button>
          )}
        </div>
        {error && (
          <span className="text-micro text-red-500 font-medium leading-snug mt-2 block">
            {error}
          </span>
        )}

        {suggestions.length > 0 && (
          <div className="mt-3 mb-5 p-3 md:p-4 border border-gray-300 rounded-2xl bg-white shadow-sm flex flex-wrap gap-2 md:gap-3 justify-left">
            {suggestions.map((s, i) => (
              <div
                key={i}
                onClick={() => handleSuggestionSelect(s)}
                className="cursor-pointer transition-all duration-200 h-6 min-w-[69px] inline-flex items-center justify-center border border-primary rounded-2xl text-body md:text-body font-semibold text-primary bg-[#F1F8F4] px-1.5 hover:text-primary hover:bg-[#e4f3e9] hover:border-primary focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)] focus:ring-offset-1"
              >
                {s}
              </div>
            ))}
          </div>
        )}

        {!homepageMode && selectedPostcode && !isAddressesLoading && (
          <div className="mt-3">
            {selectedAddress && !isEditingAddress && !hideSelectedCard ? (
              <div
                onClick={() => {
                  setIsEditingAddress(true);
                  setAddressSearch('');
                  setShowAddressDropdown(true);
                }}
                className="mt-3 cursor-pointer py-2 px-5 md:p-5 border border-primary text-body md:text-body rounded-md flex justify-between items-center h-full min-h-[55px] md:min-h-[85px]"
              >
                <div className="flex-1">
                  <div className="font-semibold text-black text-body sm:text-body leading-tight mb-0 sm:mb-1">
                    {(() => {
                      const fullAddress = formatEfxAddress(selectedAddress).label;
                      const parts = fullAddress.split(',').map((part) => part.trim());
                      return parts.length >= 2 ? `${parts[0]}, ${parts[1]}` : parts[0];
                    })()}
                  </div>
                  <div className="text-black text-body sm:text-body leading-tight">
                    {(() => {
                      const fullAddress = formatEfxAddress(selectedAddress).label;
                      const parts = fullAddress.split(',').map((part) => part.trim());
                      return parts.length >= 3
                        ? parts.slice(2).join(', ')
                        : parts.length === 2
                          ? parts[1]
                          : '';
                    })()}
                  </div>
                </div>
                <button
                  type="button"
                  className="flex items-center justify-center gap-1 sm:gap-2 text-body sm:text-body text-black hover:text-gray-700 transition-colors shrink-0 ml-4 underline decoration-[var(--color-primary)] decoration-2 underline-offset-4"
                >
                  <img src={editIcon} alt="edit" className="w-3 h-3 sm:w-4 sm:h-4" />
                  Edit
                </button>
              </div>
            ) : (
              <>
                <div className="relative">
                  <Input
                    ref={addressInputRef}
                    type="text"
                    value={addressSearch}
                    onChange={handleAddressInputChange}
                    onClick={handleAddressInputClick}
                    placeholder="Search your address..."
                    className="w-full px-3 md:px-4 py-2 md:py-3 text-body md:text-body border-1 border-green-500 rounded-lg bg-white focus:outline-none focus:ring-0 focus:border-green-500 cursor-pointer"
                  />
                  <div
                    className="absolute inset-y-0 right-3 md:right-4 flex items-center cursor-pointer"
                    onClick={handleArrowClick}
                  >
                    <svg
                      className={`w-4 h-4 md:w-5 md:h-5 text-gray-700 transition-transform duration-200 ${
                        showAddressDropdown ? 'rotate-180' : ''
                      }`}
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth={2}
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M6 9l6 6 6-6" />
                    </svg>
                  </div>
                </div>

                {addresses.length > 0 && (
                  <div className="mt-2 mb-8 bg-white border border-gray-300 rounded-lg shadow-sm">
                    <div className="h-[192px] md:h-[179px] overflow-y-auto [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-track]:bg-gray-100 [&::-webkit-scrollbar-thumb]:bg-primary [&::-webkit-scrollbar-thumb]:rounded-full">
                      {filteredAddresses.length > 0 ? (
                        filteredAddresses.map((addr, idx) => (
                          <div
                            key={idx}
                            onClick={() => handleAddressSelect(addr.value)}
                            className={`cursor-pointer p-2 text-body md:text-body text-gray-800 border-b border-gray-100 last:border-b-0 transition-colors flex items-center justify-between ${
                              selectedAddress && selectedAddress.addressID === addr.value.addressID
                                ? 'bg-primary-light'
                                : 'hover:bg-gray-50'
                            }`}
                          >
                            <span>{addr.label}</span>
                            {selectedAddress &&
                              selectedAddress.addressID === addr.value.addressID && (
                                <svg
                                  className="w-4 h-4 text-primary flex-shrink-0"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  stroke="currentColor"
                                  strokeWidth={3}
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                >
                                  <polyline points="20,6 9,17 4,12" />
                                </svg>
                              )}
                          </div>
                        ))
                      ) : (
                        <div className="px-3 md:px-4 py-2 md:py-3 text-body text-gray-600">
                          No address found for "{addressSearch}"
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        )}
        {isLoading && (selectedPostcode || postcode.trim()) && (
          <div className="mt-3 p-4 flex justify-center">
            <LoadingDots />
          </div>
        )}
      </>
    );
  }
);

export default PostcodeElement;

const formatEfxAddress = (val: EquifaxAddress) => {
  const {
    addressID,
    country,
    county,
    district,
    name,
    number,
    poBox,
    postcode,
    postTown,
    street1,
    street2,
    subBuilding,
  } = val || {};

  const address = [];
  if (poBox) address.push(poBox);
  if (subBuilding) address.push(subBuilding);
  if (name) address.push(name);
  if (number) address.push(number);
  if (street1) address.push(street1);
  if (street2) address.push(street2);
  if (district) address.push(district);
  if (postTown) address.push(postTown);
  if (postcode) address.push(postcode);
  if (county) address.push(county);
  if (country) address.push(country);

  const addressLabel = address.join(', ');
  return {
    label: addressLabel,
    value: { address: addressLabel, addressID: addressID, ...val },
  };
};
